import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { buildFloorStack, extraSlabs, FloorBand, FloorBandKind } from '../../../domain/floorStack';

/**
 * A section through the building, drawn from the answers on this step.
 *
 * Read-only and derived — nothing here is an input, and it is not part of what Submit
 * saves. It exists because the floor fields are a list of counts and radio choices that is
 * hard to picture: "multiple ground, mezzanine on the upper ground, three upper floors,
 * one basement" is a building, and a mistake in it is far easier to see than to read.
 */

/** Every band is tinted from the same primary hue so the stack reads as one object. */
const BAND_STYLES: Record<
  FloorBandKind,
  (theme: Theme) => { bg: string; border: string; dashed?: boolean; inset?: boolean }
> = {
  // On the roof and not a full floor: drawn short and dashed, like the provision bands.
  staircase: (theme) => ({
    bg: alpha(theme.palette.primary.light, 0.08),
    border: alpha(theme.palette.primary.light, 0.4),
    inset: true,
  }),
  additional: (theme) => ({
    bg: 'transparent',
    border: alpha(theme.palette.primary.light, 0.35),
    // A provision is a floor that may be built later, so it is drawn as an outline.
    dashed: true,
  }),
  // The slab over the top floor: a thin solid band, so it reads as a lid rather than a storey.
  roof: (theme) => ({
    bg: alpha(theme.palette.primary.main, 0.12),
    border: alpha(theme.palette.primary.main, 0.45),
  }),
  upper: (theme) => ({
    bg: alpha(theme.palette.primary.light, 0.1),
    border: alpha(theme.palette.primary.light, 0.3),
  }),
  // Mezzanines are part-floors: narrower, so they read as sitting inside the level below.
  mezzanine: (theme) => ({
    bg: alpha(theme.palette.primary.light, 0.06),
    border: alpha(theme.palette.primary.light, 0.25),
    inset: true,
  }),
  ground: (theme) => ({
    bg: alpha(theme.palette.primary.light, 0.18),
    border: alpha(theme.palette.primary.light, 0.45),
  }),
  parking: (theme) => ({
    bg: alpha(theme.palette.primary.light, 0.13),
    border: alpha(theme.palette.primary.light, 0.35),
  }),
  // Below grade: no fill, so the ground line reads as the surface.
  basement: (theme) => ({
    bg: alpha(theme.palette.text.primary, 0.04),
    border: alpha(theme.palette.text.primary, 0.2),
  }),
  // Footings, below the lowest level: outlined like the basements, in the earth.
  foundation: (theme) => ({
    bg: 'transparent',
    border: alpha(theme.palette.text.primary, 0.3),
    dashed: true,
  }),
};

export default function FloorDiagram() {
  const { watch, getValues } = useFormContext();

  // Watch only the fields the drawing depends on, so typing in Notes does not redraw it.
  const watched = watch([
    'workAge',
    'workCategory',
    'hasBasement',
    'basementCount',
    'groundLevelType',
    'singleGroundUsage',
    'singleHasMezzanine',
    'lowerGroundUsage',
    'lowerGroundHasMezzanine',
    'upperGroundUsage',
    'upperGroundHasMezzanine',
    'upperFloorsCount',
    'upperFloorGroups',
    'hasAdditionalFloors',
    'additionalFloorsCount',
    'workCompletedUpto',
    'hasStaircaseOhwt',
    'staircaseOhwtCount',
    'slabsCount',
    'lowerGroundRequiresColumnFooting',
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bands = useMemo(() => buildFloorStack(getValues()), [getValues, watched]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extra = useMemo(() => extraSlabs(getValues()), [getValues, watched]);

  // The surface sits above the first band below grade — a basement, or the footings.
  const firstBasement = bands.findIndex((band) => band.kind === 'basement' || band.kind === 'foundation');
  const hasExisting = bands.some((band) => band.isExisting);

  return (
    <Box className='column gap-3'>
      <Typography variant='body1' color='text.primary'>
        Key Section
      </Typography>

      {bands.length === 0 ? (
        <Typography variant='body2' color='text.disabled'>
          Fill in the floor details to see the building drawn here.
        </Typography>
      ) : (
        <>
          {/* The left padding is the gutter the drawing numbers sit in, beside the stack. */}
          <Stack gap={`${ROW_GAP}px`} sx={{ pl: `${GUTTER}px` }}>
            {bands.map((band, index) => (
              <Box key={band.key}>
                {/* The surface line, drawn once, just above the first basement. */}
                {index === firstBasement && <GroundLine />}
                <Box sx={{ position: 'relative' }}>
                  <FloorRow band={band} />
                  {band.drawings && <DrawingTag text={band.drawings} />}
                </Box>
              </Box>
            ))}
            {/* No basements: the surface sits under the whole stack instead. */}
            {firstBasement === -1 && <GroundLine />}
          </Stack>

          {hasExisting && (
            <Typography variant='caption' color='text.secondary'>
              Hatched levels are the work already completed on site; they take no new drawings.
            </Typography>
          )}
          {extra && (
            <Typography variant='caption' color='text.secondary'>
              {extra} (extra slabs asked for) belong to no single level.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

/** Width of the gutter left of the stack, and the space between two levels. */
const GUTTER = 52;
const ROW_GAP = 4;

/**
 * A level's drawing numbers, circled in the gutter on the line under that level: a slab is
 * drawn at the level's base, so S1 sits between the lowest level and the footings below it.
 */
function DrawingTag({ text }: { text: string }) {
  return (
    <Box
      sx={(theme) => ({
        position: 'absolute',
        right: '100%',
        mr: 0.75,
        bottom: 0,
        // Centred on the gap below the row, not on the row's own bottom edge.
        transform: `translateY(calc(50% + ${ROW_GAP / 2}px))`,
        zIndex: 1,
        px: 0.75,
        minWidth: 26,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        border: `1.5px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      })}
    >
      {text}
    </Box>
  );
}

function GroundLine() {
  return (
    <Stack direction='row' alignItems='center' gap={1} sx={{ my: 0.5 }}>
      <Box
        sx={{
          flex: 1,
          height: 0,
          borderBottom: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
        }}
      />
      <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
        Ground level
      </Typography>
    </Stack>
  );
}

function FloorRow({ band }: { band: FloorBand }) {
  const style = BAND_STYLES[band.kind];

  return (
    <Box
      sx={(theme) => {
        const { bg, border, dashed, inset } = style(theme);
        return {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          minHeight: 34,
          px: 1.5,
          py: 0.75,
          borderRadius: '4px',
          border: `1px ${dashed ? 'dashed' : 'solid'} ${border}`,
          backgroundColor: bg,
          // Mezzanines sit inside the level below, so they are drawn short.
          ml: inset ? 4 : 0,
          // Existing floors get a hatch so new work is distinguishable at a glance.
          backgroundImage: band.isExisting
            ? `repeating-linear-gradient(45deg, ${alpha(
                theme.palette.text.primary,
                0.07
              )} 0 6px, transparent 6px 12px)`
            : 'none',
        };
      }}
    >
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant='body2' color='text.primary' sx={{ whiteSpace: 'nowrap' }}>
          {band.name}
        </Typography>
        {band.detail && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {band.detail}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
